import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import { BackupConfiguration, BackupConfigurationCreate } from '../../types/api';
import { getApiErrorMessage } from '../../constants/messages';
import { Modal, Button } from '../ui';
import Logger from '../../utils/logger';
import { useTranslation } from 'react-i18next';

interface BackupConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  deploymentId: string;
  currentConfig?: BackupConfiguration | null;
  onConfigured: () => void;
}

const BackupConfigModal: React.FC<BackupConfigModalProps> = ({
  isOpen,
  onClose,
  deploymentId,
  currentConfig,
  onConfigured
}) => {
  const { t } = (useTranslation as any)('backups');
  const [schedule, setSchedule] = useState('0 2 * * *'); // Daily at 2 AM by default
  const [retentionPolicy, setRetentionPolicy] = useState(7);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentConfig) {
      setSchedule(currentConfig.schedule);
      // Parse retention_policy from "7d" format to number
      const retentionDays = parseInt(currentConfig.retention_policy.replace('d', ''));
      setRetentionPolicy(retentionDays);
      setEnabled(currentConfig.enabled);
    }
  }, [currentConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      const config: BackupConfigurationCreate = {
        schedule,
        retention_policy: `${retentionPolicy}d`,
        enabled
      };

      await apiService.configureBackup(deploymentId, config);
      toast.success(t('configModal.configSuccess') as string);
      onConfigured();
      onClose();
    } catch (error: unknown) {
      Logger.error('Error configuring backups:', error);
      toast.error(getApiErrorMessage(error) || t('configModal.configError') as string);
    } finally {
      setLoading(false);
    }
  };

  const schedulePresets = [
    { label: 'Quotidien (2h du matin)', value: '0 2 * * *' },
    { label: 'Toutes les 12 heures', value: '0 */12 * * *' },
    { label: 'Toutes les 6 heures', value: '0 */6 * * *' },
    { label: 'Hebdomadaire (dimanche 2h)', value: '0 2 * * 0' }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('configModal.title') as string} size="lg">
      <form onSubmit={handleSubmit}>
        <div className="p-6 space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-text-primary">
                {t('configModal.enableAutoBackups')}
              </label>
              <p className="text-xs text-text-tertiary mt-1">
                {t('configModal.enableAutoHelp')}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-4 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-1 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-600"></div>
            </label>
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {t('configModal.scheduleCron')}
            </label>
            <select
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              className="w-full px-3 py-2 bg-surface-3 text-text-primary rounded border border-border-1 focus:outline-none focus:ring-2 focus:ring-accent-500"
            >
              {schedulePresets.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-tertiary mt-2">
              Expression actuelle: <code className="bg-surface-3 px-1 rounded">{schedule}</code>
            </p>
          </div>

          {/* Retention Policy */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {t('configModal.retentionDays')}
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={retentionPolicy}
              onChange={(e) => setRetentionPolicy(parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-surface-3 text-text-primary rounded border border-border-1 focus:outline-none focus:ring-2 focus:ring-accent-500"
              required
            />
            <p className="text-xs text-text-tertiary mt-2">
              Les backups plus anciens que {retentionPolicy} jours seront automatiquement supprimés
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-blue-500/12 border border-blue-500/20 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-400 mb-2">
              {t('configModal.importantInfo')}
            </h4>
            <ul className="text-xs text-blue-400 space-y-1 list-disc list-inside">
              <li>Les backups sont stockés dans votre bucket S3 configuré</li>
              <li>Le chemin S3 est généré automatiquement pour chaque déploiement</li>
              <li>Vous pouvez créer des backups manuels à tout moment</li>
              <li>La restauration se fait via une architecture Blue/Green sans interruption</li>
            </ul>
          </div>
        </div>

        {/* Actions - sticky at bottom */}
        <div className="flex justify-end gap-3 p-4 border-t border-border-1 sticky bottom-0 bg-surface-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            {t('common:cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {t('common:save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default BackupConfigModal;
